import type { FC, ReactNode } from 'react'
import type { StaffBoardEntry } from '@/lib/carwash/selectors'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/kit/avatar'
import { MINUTE, formatMinutes, toTime } from '@/lib/carwash/format'
import { plateLabel } from '@/lib/carwash/plate'

export const WorkloadMeter: FC<{ value: number, max: number, className?: string }> = ({ value, max, className }): ReactNode => {
    const segments = 5
    const filled = max > 0 ? Math.round((value / max) * segments) : 0

    return (
        <span className={cn('inline-flex items-center gap-0.5', className)} aria-hidden='true'>
            {Array.from({ length: segments }, (_, index) => (
                <span
                    key={index}
                    className={cn('h-1.5 w-2.5 rounded-full', index < filled ? 'bg-primary/70' : 'bg-slate-200')}
                />
            ))}
        </span>
    )
}

export const describeStaffEntry = (entry: StaffBoardEntry, now: number): string => {
    if (entry.status === 'busy') {
        const current = entry.currentJob
        const freeIn = entry.freeInMinutes ?? 0
        const plate = current ? plateLabel(current.plate, `#${current.number}`) : 'a vehicle'
        return freeIn > 0 ? `On ${plate}, free in about ${formatMinutes(freeIn)}` : `On ${plate}, finishing now`
    }

    if (entry.status === 'break') {
        const minutes = Math.max(0, Math.floor((now - toTime(entry.staff.dutySince)) / MINUTE))
        return `On break for ${formatMinutes(minutes)}`
    }

    if (entry.status === 'inactive') return 'Inactive'

    const idle = entry.idleSince ? Math.max(0, Math.floor((now - entry.idleSince) / MINUTE)) : 0
    return idle >= 1 ? `Free for ${formatMinutes(idle)}` : 'Just finished'
}

interface StaffChoiceProps {
    entry: StaffBoardEntry
    selected: boolean
    onSelect: () => void
    now: number
    maxToday: number
    tag?: ReactNode
}

/* A selectable staff row with availability and today's workload. */
export const StaffChoice: FC<StaffChoiceProps> = ({
    entry,
    selected,
    onSelect,
    now,
    maxToday,
    tag
}): ReactNode => {
    const muted = entry.status !== 'available'

    return (
        <button
            type='button'
            role='radio'
            aria-checked={selected}
            onClick={onSelect}
            className={cn(
                'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-[background-color,border-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                selected
                    ? 'border-primary bg-accent/70 shadow-[0_0_0_1px_var(--color-primary)]'
                    : 'border-border bg-card hover:border-slate-300 hover:bg-slate-50'
            )}
        >
            <span className='relative'>
                <Avatar name={entry.staff.name} photo={entry.staff.photo} size='md' className={cn(muted && !selected && 'opacity-80')} />
                <span
                    className={cn(
                        'absolute -right-0.5 -bottom-0.5 size-3.5 rounded-full ring-2 ring-white',
                        entry.status === 'available' && 'bg-emerald-500',
                        entry.status === 'busy' && 'bg-sky-500',
                        entry.status === 'break' && 'bg-amber-500',
                        entry.status === 'inactive' && 'bg-slate-400'
                    )}
                    aria-hidden='true'
                />
            </span>

            <span className='min-w-0 flex-1'>
                <span className='flex items-center gap-2'>
                    <span className='truncate text-[15px] font-semibold text-foreground'>
                        {entry.staff.name}
                    </span>

                    {tag}
                </span>

                <span className='mt-0.5 block truncate text-[13px] text-muted-foreground' suppressHydrationWarning>
                    {describeStaffEntry(entry, now)}
                </span>

                <span className='mt-1.5 flex items-center gap-2 text-xs font-medium text-slate-500'>
                    <WorkloadMeter value={entry.completedToday} max={maxToday} />
                    {entry.completedToday === 1 ? '1 car today' : `${entry.completedToday} cars today`}
                </span>
            </span>

            <span
                className={cn(
                    'grid size-6 shrink-0 place-items-center rounded-full border-2 transition-colors',
                    selected ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white'
                )}
                aria-hidden='true'
            >
                {selected && <Check className='size-3.5' strokeWidth={3} />}
            </span>
        </button>
    )
}
